import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import { money, statusLabel, statusColor, padNum } from "@/lib/format";
import { shareRaffle, shareTicket } from "@/lib/share";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ArrowLeft, ExternalLink, Play, CheckCircle2, XCircle, Trophy, Rocket, Lock, Ban, FileText, Eye, ShieldCheck, Trash2, Archive, ArchiveRestore, Share2, Search, Package, Send, CalendarClock, RotateCcw } from "lucide-react";
import ConfirmDialog from "@/components/ConfirmDialog";

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-CR", { day: "2-digit", month: "short", year: "numeric" }) +
      " · " + d.toLocaleTimeString("es-CR", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function RaffleManage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = can(user, "manage_raffles");
  const canVerify = can(user, "verify_payments");
  const canDraw = can(user, "execute_draw");
  const { raffleId } = useParams();
  const [raffle, setRaffle] = useState(null);
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [drawOpen, setDrawOpen] = useState(false);
  const [winningInput, setWinningInput] = useState("");
  const [proofView, setProofView] = useState(null);
  const [delOpen, setDelOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [delLoading, setDelLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchMode, setSearchMode] = useState("nombre");
  const [allOrders, setAllOrders] = useState([]);
  const [searching, setSearching] = useState(false);
  const [resendOrder, setResendOrder] = useState(null);
  const [resendEmail, setResendEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [delOrder, setDelOrder] = useState(null);
  const [delOrderLoading, setDelOrderLoading] = useState(false);
  const openSearch = async () => {
    setSearchQuery(""); setAllOrders([]); setSearchOpen(true); setSearching(true);
    try { const res = await api.get(`/raffles/${raffleId}/orders`); setAllOrders(res.data || []); }
    catch (e) { toast.error("No se pudieron cargar los registros"); }
    finally { setSearching(false); }
  };

  const searchDigits = (searchQuery || "").replace(/\D/g, "");
  const searchedNum = searchMode === "numero" && searchDigits ? Number(searchDigits) : null;

  const searchResults = (() => {
    const q = (searchQuery || "").trim().toLowerCase();
    if (q.length < 1) return null;
    const digits = q.replace(/\D/g, "");
    return allOrders
      .filter((o) => {
        const name = (o.buyer_name || "").toLowerCase();
        const email = (o.buyer_email || "").toLowerCase();
        const phone = (o.buyer_phone || "").replace(/\D/g, "");
        if (searchMode === "correo") return email.includes(q);
        if (searchMode === "telefono") return digits.length >= 8 && phone.includes(digits);
        if (searchMode === "numero") return digits.length >= 1 && (o.numbers || []).some((n) => n === Number(digits));
        return name.includes(q); // "nombre"
      })
      .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  })();

  const searchPlaceholder = searching
    ? "Cargando registros..."
    : ({ nombre: "Nombre del comprador", correo: "correo@ejemplo.com", telefono: "Teléfono (mín. 8 dígitos)", numero: "Número del evento (ej. 0024)" }[searchMode]);

  const deleteRaffle = async () => {
    setDelLoading(true);
    try {
      await api.delete(`/raffles/${raffleId}`);
      toast.success("Rifa eliminada");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo eliminar la rifa");
    } finally { setDelLoading(false); }
  };

  const load = useCallback(async () => {
    const r = await api.get(`/raffles/${raffleId}`);
    setRaffle(r.data);
  }, [raffleId]);

  const loadOrders = useCallback(async () => {
    const params = filter === "all" ? {} : { status: filter };
    const o = await api.get(`/raffles/${raffleId}/orders`, { params });
    setOrders(o.data);
  }, [raffleId, filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadOrders(); }, [loadOrders]);

  if (!raffle) return <DashboardLayout title="Cargando..."><p className="text-neutral-400">Cargando...</p></DashboardLayout>;

  const cur = raffle.currency || "₡";
  const p = raffle.progress || {};
  const hasSales = (p.sold ?? 0) > 0;
  const isSuper = !!user?.is_super_admin;

  const changeStatus = async (status) => {
    try {
      if (status === "active" && raffle.status === "draft") await api.post(`/raffles/${raffleId}/activate`);
      else await api.post(`/raffles/${raffleId}/status`, { status });
      toast.success("Estado actualizado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const confirmCancel = async () => {
    setCancelLoading(true);
    try {
      await api.post(`/raffles/${raffleId}/status`, { status: "cancelled" });
      toast.success("Rifa cancelada");
      setCancelOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cancelar la rifa");
    } finally { setCancelLoading(false); }
  };

  const reactivateRaffle = async () => {    try {
      const res = await api.post(`/raffles/${raffleId}/reactivate`);
      toast.success(`${res.data.status === "active" ? "Venta reanudada" : "Evento reactivado"} (${statusLabel(res.data.status)}). Se conservaron participantes, números y pagos.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo reactivar el evento"); }
  };

  const approve = async (id) => {
    try { await api.post(`/orders/${id}/approve`); toast.success("Pedido aprobado, correo enviado"); load(); loadOrders(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const doReject = async () => {
    try { await api.post(`/orders/${rejectId}/reject`, { reason: rejectReason }); toast.success("Pedido rechazado"); setRejectId(null); setRejectReason(""); load(); loadOrders(); }
    catch (e) { toast.error("Error"); }
  };
  const openResend = (o) => { setResendOrder(o); setResendEmail(o.buyer_email || ""); };  const doResend = async () => {
    if (!resendEmail.trim() || !resendEmail.includes("@")) { toast.error("Ingresa un correo válido"); return; }
    setResendLoading(true);
    try {
      await api.post(`/orders/${resendOrder.id}/resend-email`, { email: resendEmail.trim() });
      toast.success(`Correo reenviado a ${resendEmail.trim()}`);
      setResendOrder(null);
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo reenviar el correo"); }
    finally { setResendLoading(false); }
  };

  const doDeleteOrder = async () => {
    if (!delOrder) return;
    setDelOrderLoading(true);
    try {
      await api.delete(`/orders/${delOrder.id}`);
      toast.success("Registro eliminado, números disponibles nuevamente");
      setDelOrder(null);
      load(); loadOrders();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo eliminar el registro"); }
    finally { setDelOrderLoading(false); }
  };

  const executeDraw = async () => {
    try {
      let body = {};
      if (raffle.draw_method !== "random") {
        const nums = winningInput.split(/[\s,]+/).filter(Boolean).map(Number);
        const need = raffle.winners_count || 1;
        if (nums.some((n) => Number.isNaN(n))) { toast.error("Ingresa solo números válidos"); return; }
        if (nums.length !== need) { toast.error(`Debes ingresar ${need} número(s) ganador(es), separados por espacio o coma`); return; }
        body = { winning_numbers: nums };
      }
      await api.post(`/raffles/${raffleId}/draw`, body);
      toast.success("¡Sorteo ejecutado!");
      setDrawOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al sortear"); }
  };

  return (
    <DashboardLayout title="Gestionar rifa">
      <Button variant="ghost" onClick={() => navigate("/dashboard")} className="mb-4 rounded-xl -ml-2"><ArrowLeft size={16} className="mr-1" />Volver al panel</Button>

      <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden mb-6 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
        <div className="p-6 flex flex-col md:flex-row gap-6">
          <div className="w-full md:w-40 h-40 rounded-xl overflow-hidden bg-neutral-100 flex-shrink-0">
            {raffle.image_urls?.[0] ? <img src={raffle.image_urls[0]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-neutral-300"><FileText size={36} /></div>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusColor(raffle.status)}`}>{statusLabel(raffle.status)}</span>
              <span className="text-xs text-neutral-400">{raffle.draw_method === "random" ? "Aleatorio verificable" : raffle.draw_method === "manual" ? "Manual" : "Vinculado a Lotería Nacional"}</span>
            </div>
            <h1 className="font-display font-extrabold text-2xl mt-2">{raffle.title}</h1>
            <p className="text-neutral-500 text-sm mt-1">{money(raffle.price_per_number, cur)} por número · {raffle.total_numbers} números</p>
            {raffle.seed_code && <p className="text-xs text-neutral-400 mt-1" data-testid="raffle-seed-code">Código semilla: <span className="font-mono font-bold text-neutral-600 tracking-wider">{raffle.seed_code}</span></p>}
            <div className="mt-4 max-w-md">
              <div className="flex justify-between text-xs text-neutral-500 mb-1">
                <span>{p.sold ?? 0} vendidos · {p.pending ?? 0} en verificación · {p.reserved ?? 0} reservados</span>
                <span className="font-semibold">{p.pct ?? 0}%</span>
              </div>
              <Progress value={p.pct ?? 0} className="h-2.5" />
            </div>
          </div>
        </div>
        <div className="border-t border-neutral-100 p-4 flex flex-wrap gap-2">
          {raffle.status === "draft" && canManage && <Button data-testid="activate-btn" onClick={() => changeStatus("active")} className="bg-[#00AEEF] hover:bg-[#0092CC] rounded-xl"><Rocket size={16} className="mr-1" />Activar y generar números</Button>}
          {raffle.status === "draft" && canManage && <Button variant="outline" onClick={() => navigate(`/dashboard/raffles/${raffleId}/editar`)} className="rounded-xl">Editar</Button>}
          {raffle.status === "active" && canManage && <Button variant="outline" onClick={() => navigate(`/dashboard/raffles/${raffleId}/editar`)} className="rounded-xl">Editar</Button>}
          {raffle.status === "active" && canManage && <Button data-testid="close-btn" onClick={() => changeStatus("closed")} variant="outline" className="rounded-xl"><Lock size={16} className="mr-1" />Cerrar ventas</Button>}
          {(raffle.status === "active" || raffle.status === "closed") && canDraw && <Button data-testid="open-draw-btn" onClick={() => setDrawOpen(true)} className="bg-neutral-900 hover:bg-neutral-800 rounded-xl"><Play size={16} className="mr-1" />Ejecutar sorteo</Button>}
          {raffle.status !== "draft" && <a href={`/r/${raffleId}`} target="_blank" rel="noreferrer"><Button variant="outline" className="rounded-xl"><ExternalLink size={16} className="mr-1" />Ver página pública</Button></a>}
          {raffle.status !== "draft" && <Button variant="outline" data-testid="share-raffle-btn" onClick={() => shareRaffle(raffleId, raffle.title)} className="rounded-xl border-[#00AEEF] text-[#0092CC] hover:bg-[#E6F7FE]"><Share2 size={16} className="mr-1" />Compartir enlace</Button>}
          {raffle.status !== "cancelled" && raffle.status !== "drawn" && raffle.status !== "archived" && canManage && <Button data-testid="cancel-btn" onClick={() => setCancelOpen(true)} variant="ghost" className="rounded-xl text-red-500"><Ban size={16} className="mr-1" />Cancelar</Button>}
          {raffle.status === "cancelled" && isSuper && <Button data-testid="reactivate-btn" onClick={reactivateRaffle} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"><RotateCcw size={16} className="mr-1" />Reactivar evento</Button>}
          {raffle.status === "closed" && isSuper && <Button data-testid="resume-sales-btn" onClick={reactivateRaffle} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"><RotateCcw size={16} className="mr-1" />Reanudar venta</Button>}
          {["active", "closed", "drawn", "cancelled"].includes(raffle.status) && canManage && <Button data-testid="archive-btn" onClick={() => changeStatus("archived")} variant="ghost" className="rounded-xl text-neutral-600"><Archive size={16} className="mr-1" />Archivar</Button>}
          {raffle.status === "archived" && canManage && <Button data-testid="restore-btn" onClick={() => changeStatus("closed")} variant="outline" className="rounded-xl"><ArchiveRestore size={16} className="mr-1" />Restaurar</Button>}
          {canManage && (!hasSales || isSuper) && <Button data-testid="delete-raffle-btn" onClick={() => setDelOpen(true)} variant="ghost" className="rounded-xl text-red-500"><Trash2 size={16} className="mr-1" />Eliminar</Button>}
          {canManage && hasSales && !isSuper && <span className="text-xs text-neutral-400 self-center ml-1" data-testid="delete-locked-note">Con ventas: solo el super-admin puede eliminar (código {raffle.seed_code})</span>}
        </div>
      </div>

      <ConfirmDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title="¿Seguro que deseas cancelar esta rifa?"
        description={`La rifa "${raffle.title}" dejará de estar disponible para el público y no se podrán recibir más compras. Se conservan los participantes, números vendidos y pagos registrados. Solo el super-administrador puede reactivarla después.`}
        confirmText="Sí, cancelar rifa"
        onConfirm={confirmCancel}
        loading={cancelLoading}
      />

      <ConfirmDialog
        open={delOpen}
        onOpenChange={setDelOpen}
        title="¿Desea realmente realizar esta acción?"
        description={`Se eliminará la rifa "${raffle.title}" con todos sus números y pedidos. Esta acción no se puede deshacer.`}
        confirmText="Sí, eliminar rifa"
        onConfirm={deleteRaffle}
        loading={delLoading}
      />

      <ConfirmDialog
        open={!!delOrder}
        onOpenChange={(o) => !o && setDelOrder(null)}
        title="¿Eliminar registro?"
        description="¿Estás seguro de que deseas eliminar este registro? El participante perderá todos sus números asignados y quedarán disponibles nuevamente."
        confirmText="Sí, eliminar"
        onConfirm={doDeleteOrder}
        loading={delOrderLoading}
      />

      {raffle.status === "drawn" && (raffle.winners?.length || raffle.winning_number != null) && (
        <div className="bg-gradient-to-br from-amber-50 to-white border border-amber-200 rounded-2xl p-6 mb-6" data-testid="draw-result">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-amber-400 text-amber-950 flex items-center justify-center"><Trophy size={24} /></div>
            <p className="text-sm font-semibold text-amber-800">{(raffle.winners?.length || 1) > 1 ? "Números ganadores" : "Número ganador"}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {(raffle.winners?.length ? raffle.winners : [{ place: 1, number: raffle.winning_number, buyer_name: null }]).map((w) => (
              <div key={w.place} data-testid={`winner-${w.place}`} className="bg-white rounded-xl border border-amber-200 p-3">
                <p className="text-xs font-bold uppercase tracking-wide text-amber-600">{w.place === 1 ? "🥇 1er lugar" : w.place === 2 ? "🥈 2º lugar" : w.place === 3 ? "🥉 3er lugar" : `${w.place}º lugar`}</p>
                <p className="font-display font-extrabold text-2xl text-amber-900">#{padNum(w.number)}</p>
                {w.buyer_name && <p className="text-xs text-neutral-500 mt-1 truncate">{w.buyer_name}</p>}
              </div>
            ))}
          </div>
          {raffle.draw_seed && (
            <div className="mt-4 text-xs text-neutral-500 bg-white rounded-xl p-3 border border-neutral-200 flex items-start gap-2">
              <ShieldCheck size={14} className="mt-0.5 text-[#00AEEF]" />
              <div>
                <p><b>Semilla:</b> <code className="break-all">{raffle.draw_seed}</code></p>
                <p className="mt-1"><b>Hash SHA-256:</b> <code className="break-all">{raffle.draw_hash}</code></p>
                <p className="mt-1 text-neutral-400">Ejecutado: {new Date(raffle.draw_executed_at).toLocaleString("es-CR")}</p>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 mb-3">
        <h2 className="font-display font-bold text-xl">Registro de números</h2>
        {raffle.status !== "draft" && (
          <Button data-testid="open-number-search-btn" variant="outline" size="icon" onClick={openSearch} className="rounded-lg h-8 w-8" title="Buscar registro">
            <Search size={16} />
          </Button>
        )}
      </div>
      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="rounded-xl max-sm:w-full max-sm:h-auto max-sm:grid max-sm:grid-cols-2 max-sm:gap-1 max-sm:p-1.5">
          <TabsTrigger value="pending" data-testid="tab-pending" className="max-sm:h-10 max-sm:text-xs">Por verificar</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved" className="max-sm:h-10 max-sm:text-xs">Aprobados</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected" className="max-sm:h-10 max-sm:text-xs">Rechazados</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all" className="max-sm:h-10 max-sm:text-xs">Todos</TabsTrigger>
        </TabsList>
        <TabsContent value={filter} className="mt-4">
          {orders.length === 0 ? (
            <p className="text-neutral-400 py-8 text-center">Sin pedidos en esta categoría.</p>
          ) : (
            <div className="space-y-3">
              {orders.map((o) => (
                <div key={o.id} className="bg-white rounded-2xl border border-neutral-200 p-5" data-testid={`order-${o.id}`}>
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold">{o.buyer_name}</p>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusColor(o.status)}`}>{statusLabel(o.status)}</span>
                      </div>
                      <p className="text-sm text-neutral-500">{o.buyer_email} · {o.buyer_phone}</p>
                      <p className="text-xs text-neutral-400 mt-0.5 flex items-center gap-1" data-testid={`order-date-${o.id}`}>
                        <CalendarClock size={12} /> Compra: {fmtDateTime(o.created_at)}
                      </p>
                      {o.package_summary && (
                        <div className="flex items-center gap-1.5 mt-1.5" data-testid={`order-package-${o.id}`}>
                          <Package size={13} className="text-[#00AEEF]" />
                          <span className="text-xs font-medium text-[#0092CC]">{o.package_summary}</span>
                          <span className="text-xs text-neutral-400">· {o.numbers.length} número(s)</span>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {o.numbers.map((n) => (
                          <span key={n} data-testid={`order-number-${o.id}-${n}`} className="text-xs font-semibold px-2 py-0.5 rounded bg-neutral-100 text-neutral-700">#{padNum(n)}</span>
                        ))}
                      </div>
                      {o.rejection_reason && <p className="text-xs text-red-500 mt-2">Motivo: {o.rejection_reason}</p>}
                    </div>
                    <div className="text-right">
                      <p className="font-display font-bold text-lg">{money(o.amount, cur)}</p>
                      {o.payment_proof_path && (
                        <Button variant="link" size="sm" data-testid={`view-proof-${o.id}`} onClick={() => setProofView(o.payment_proof_path)} className="text-[#00AEEF] h-auto p-0"><Eye size={14} className="mr-1" />Ver comprobante</Button>
                      )}
                      {canVerify && o.buyer_email && (
                        <div><Button variant="link" size="sm" data-testid={`resend-${o.id}`} onClick={() => openResend(o)} className="text-neutral-500 hover:text-[#00AEEF] h-auto p-0 mt-1"><Send size={13} className="mr-1" />Reenviar correo</Button></div>
                      )}
                      <div><Button variant="link" size="sm" data-testid={`share-ticket-${o.id}`} onClick={() => shareTicket(o.id, o.buyer_name, raffle.title)} className="text-neutral-500 hover:text-[#00AEEF] h-auto p-0 mt-1"><Share2 size={13} className="mr-1" />Compartir ticket</Button></div>
                      {canVerify && o.status === "approved" && (
                        <div><Button variant="link" size="sm" data-testid={`del-order-${o.id}`} onClick={() => setDelOrder(o)} className="text-red-500 hover:text-red-600 h-auto p-0 mt-1"><Trash2 size={13} className="mr-1" />Eliminar registro</Button></div>
                      )}
                    </div>
                  </div>
                  {canVerify && (o.status === "pending" || o.status === "reserved") && (
                    <div className="flex gap-2 mt-4 pt-4 border-t border-neutral-100">
                      <Button data-testid={`approve-${o.id}`} onClick={() => approve(o.id)} className="bg-[#00AEEF] hover:bg-[#0092CC] rounded-xl flex-1 md:flex-none"><CheckCircle2 size={16} className="mr-1" />Aprobar</Button>
                      <Button data-testid={`reject-${o.id}`} onClick={() => setRejectId(o.id)} variant="outline" className="rounded-xl flex-1 md:flex-none text-red-600 border-red-200 hover:bg-red-50"><XCircle size={16} className="mr-1" />Rechazar</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Reject dialog */}
      <Dialog open={!!rejectId} onOpenChange={(o) => !o && setRejectId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Rechazar pedido</DialogTitle></DialogHeader>
          <p className="text-sm text-neutral-500">Los números volverán a estar disponibles y se enviará un correo de anulación al comprador.</p>
          <Input data-testid="reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Motivo (ej: comprobante ilegible)" className="rounded-lg" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectId(null)} className="rounded-xl">Cancelar</Button>
            <Button data-testid="confirm-reject-btn" onClick={doReject} className="bg-red-600 hover:bg-red-700 rounded-xl">Rechazar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resend email dialog */}
      <Dialog open={!!resendOrder} onOpenChange={(o) => !o && setResendOrder(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reenviar correo del pedido</DialogTitle></DialogHeader>
          <p className="text-sm text-neutral-500">Se reenviará el correo {resendOrder?.status === "approved" ? "de confirmación (aprobado)" : "de compra en revisión"} de <b>{resendOrder?.buyer_name}</b> con sus números{resendOrder?.package_summary ? " y ticket" : ""}. Puedes cambiar el correo de destino si el comprador lo tenía mal.</p>
          <Label className="text-sm mt-2">Correo de destino</Label>
          <Input data-testid="resend-email-input" type="email" value={resendEmail} onChange={(e) => setResendEmail(e.target.value)} placeholder="correo@ejemplo.com" className="rounded-lg" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setResendOrder(null)} className="rounded-xl">Cancelar</Button>
            <Button data-testid="confirm-resend-btn" onClick={doResend} disabled={resendLoading} className="bg-[#00AEEF] hover:bg-[#0092CC] rounded-xl">{resendLoading ? "Enviando..." : "Reenviar correo"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Draw dialog */}
      <Dialog open={drawOpen} onOpenChange={setDrawOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Ejecutar sorteo</DialogTitle></DialogHeader>
          {raffle.draw_method === "random" ? (
            <p className="text-sm text-neutral-600">El sistema seleccionará <b>{raffle.winners_count || 1}</b> número(s) ganador(es) entre los <b>vendidos</b>, en orden de lugar, usando una semilla criptográfica verificable. Esta acción no se puede deshacer.</p>
          ) : (
            <div>
              <p className="text-sm text-neutral-600 mb-3">{raffle.draw_method === "external" ? "Ingresa los números ganadores según tu regla externa" : "Ingresa los números ganadores"}, en orden (1º, 2º, 3º), separados por espacio o coma.</p>
              {raffle.draw_rule_text && <p className="text-xs text-neutral-400 mb-3 bg-neutral-50 rounded-lg p-2">Regla: {raffle.draw_rule_text}</p>}
              <Input data-testid="winning-number-input" value={winningInput} onChange={(e) => setWinningInput(e.target.value)} placeholder={`Ej: ${raffle.winners_count >= 3 ? "12 45 78" : raffle.winners_count === 2 ? "12 45" : "12"} — ${raffle.winners_count || 1} número(s)`} className="rounded-lg" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDrawOpen(false)} className="rounded-xl">Cancelar</Button>
            <Button data-testid="confirm-draw-btn" onClick={executeDraw} className="bg-neutral-900 hover:bg-neutral-800 rounded-xl"><Play size={16} className="mr-1" />Sortear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={searchOpen} onOpenChange={(o) => { setSearchOpen(o); if (!o) { setSearchQuery(""); setAllOrders([]); setSearchMode("nombre"); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Buscar registro</DialogTitle></DialogHeader>
          <p className="text-sm text-neutral-500">Elige el filtro y busca al comprador. Filtra mientras escribes, en todos los estados (aprobados, rechazados, reservados y por verificar).</p>
          <div className="flex gap-2">
            <Select value={searchMode} onValueChange={(v) => setSearchMode(v)}>
              <SelectTrigger data-testid="number-search-mode" className="w-[140px] rounded-lg shrink-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="nombre" data-testid="mode-nombre">Nombre</SelectItem>
                <SelectItem value="correo" data-testid="mode-correo">Correo</SelectItem>
                <SelectItem value="telefono" data-testid="mode-telefono">Teléfono</SelectItem>
                <SelectItem value="numero" data-testid="mode-numero">Número</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <Input data-testid="number-search-input"
                type={searchMode === "telefono" || searchMode === "numero" ? "tel" : "text"}
                inputMode={searchMode === "telefono" || searchMode === "numero" ? "numeric" : "text"}
                value={searchQuery} autoFocus
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={searchPlaceholder} className="rounded-lg pl-9" />
            </div>
          </div>
          {searchMode === "telefono" && searchDigits.length > 0 && searchDigits.length < 8 && (
            <p className="text-xs text-amber-600" data-testid="phone-min-hint">Ingresa al menos 8 dígitos del teléfono.</p>
          )}
          {searchResults && (
            <div className="mt-2 space-y-2 max-h-80 overflow-auto" data-testid="number-search-result">
              {searchResults.length === 0 ? (
                <p className="text-sm text-neutral-500 py-3 text-center">No se encontraron registros con ese criterio.</p>
              ) : (
                <>
                  <p className="text-xs text-neutral-400">{searchResults.length} registro(s) encontrado(s)</p>
                  {searchResults.map((o) => (
                    <div key={o.id} className="rounded-xl border border-neutral-200 p-3" data-testid={`search-order-${o.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-sm">{o.buyer_name}</span>
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusColor(o.status)}`}>{statusLabel(o.status)}</span>
                      </div>
                      <p className="text-xs text-neutral-500 mt-0.5">{o.buyer_email} · {o.buyer_phone}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(o.numbers || []).map((n) => (
                          <span key={n}
                            data-testid={n === searchedNum ? `hl-number-${n}` : undefined}
                            className={`text-xs font-semibold px-1.5 py-0.5 rounded ${n === searchedNum ? "bg-green-100 text-green-700 ring-1 ring-green-500" : "bg-neutral-100 text-neutral-700"}`}>#{padNum(n)}</span>
                        ))}
                      </div>
                      <p className="text-xs text-neutral-400 mt-2">Monto: {money(o.amount, cur)}</p>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Proof viewer */}
      <Dialog open={!!proofView} onOpenChange={(o) => !o && setProofView(null)}>
        <DialogContent className="max-w-lg w-[95vw] max-h-[90dvh] p-0 gap-0 overflow-hidden flex flex-col">
          <DialogHeader className="px-6 py-4 border-b border-neutral-100 shrink-0 text-left pr-12">
            <DialogTitle>Comprobante de pago</DialogTitle>
          </DialogHeader>
          <div className="overflow-auto p-4 flex-1">
            {proofView && (proofView.endsWith(".pdf") ? (
              <a href={`${API}/files/${proofView}`} target="_blank" rel="noreferrer" className="text-[#00AEEF] underline">Abrir PDF</a>
            ) : (
              <img src={`${API}/files/${proofView}`} alt="Comprobante" className="w-full h-auto rounded-lg" />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
